import React, { useMemo, useImperativeHandle, useCallback } from 'react';
import styled from 'styled-components';
import { HiOutlineMicrophone } from 'react-icons/hi';
import { FaArrowRight } from 'react-icons/fa';
import { MdStop } from 'react-icons/md';
import { BiClipboard } from 'react-icons/bi';
import { GrAttachment } from 'react-icons/gr';

import TextareaAutosize from 'react-textarea-autosize';
import { FileDropzone } from '@/features/ai-agents/shared/file-dropzone';
import { ChatImageTile } from '@/features/ai-agents/components/chat-image-tile';
import { useChatFilesUpload } from '@/features/ai-agents/shared/use-chat-files-upload';
import {
  AiChatMessage,
  chatNow,
  ImageData,
} from '@/features/ai-agents/shared/models';
import { ToolWhiteboard } from '@/features/ai-agents/components/tool-whiteboard';

// chat-input.tsx

export interface ChatInputProps {
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxRows?: number;
  onSubmit?: (msg: AiChatMessage) => void;
  onAbort?: () => void;
  showDropZone?: boolean;

  enableFilesUpload?: boolean;
  enableWhiteboard?: boolean;
  enableExamples?: boolean;
  enableVoiceInput?: boolean;
}

export interface ChatInputHandle {
  /** 1) set the input value programmatically */
  setPrompt: (value: string) => void;
  /** 2) trigger the same flow as clicking "Send" / pressing Enter */
  send: () => void;
  /** 3) open the file dropzone */
  openDropzone: () => void;
  /** 4) open the whiteboard modal */
  openWhiteboard: () => void;
}

export const ChatInput = React.forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      disabled,
      loading,
      placeholder,
      maxRows,
      onSubmit,
      onAbort,
      showDropZone,
      enableFilesUpload = true,
      enableWhiteboard = true,
      enableExamples = true,
      enableVoiceInput = true,
    },
    ref,
  ) => {
    const [prompt, setPrompt] = React.useState(``);
    const {
      filesList,
      dropzoneProps,
      uploadedFiles,
      uploadedFilesRef,
      isUploading,
      onImageDelete,
      reachMaxFiles,
      clearFiles,
      addFileToUploadQueue,
    } = useChatFilesUpload();
    const [isWhiteboardOpen, setIsWhiteboardOpen] = React.useState(false);

    const isLoading = useMemo(
      () => loading || isUploading,
      [loading, isUploading],
    );

    const onSend = useCallback(() => {
      if (!isLoading && !!prompt) {
        onSubmit?.({
          role: `user`,
          content: prompt,
          created_at: chatNow(),
          images: uploadedFiles?.map((url) => ({ url } as ImageData)),
          filesToUpload: uploadedFilesRef,
        });
        setPrompt(``);
        clearFiles();
      }
    }, [
      isLoading,
      prompt,
      onSubmit,
      uploadedFiles,
      uploadedFilesRef,
      clearFiles,
    ]);

    const onWhiteboardClick = useCallback(() => {
      setIsWhiteboardOpen(true);
    }, []);

    const onStop = useCallback(() => {
      onAbort?.();
    }, [onAbort]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === `Enter` && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    };

    const placeholderText = useMemo(() => {
      if (disabled) return `Select an agent to start chatting...`;
      if (placeholder) return placeholder;
      return `Ask for anything...`;
    }, [placeholder, disabled]);

    const onUploadSketch = (file: File) => {
      addFileToUploadQueue(file);
      setPrompt(
        `Use this wireframe as a layout reference when building the widget.
  • Match structure & hierarchy exactly.
  •Replicate spacing & relationships between elements.
  •Ignore exact visual styles unless specified.
  •Make it responsive: match the layout for both mobile and desktop unless told otherwise.`,
      );
    };

    // ✨ Expose the 4 functions to parents
    useImperativeHandle(
      ref,
      () => ({
        setPrompt, // 1) set prompt
        send: onSend, // 2) trigger send
        openDropzone: () => {
          // 3) trigger dropzoneProps.open
          if (!reachMaxFiles && enableFilesUpload) {
            dropzoneProps.open?.();
          }
        },
        openWhiteboard: onWhiteboardClick, // 4) trigger whiteboard
      }),
      [
        setPrompt,
        onSend,
        dropzoneProps.open,
        reachMaxFiles,
        enableFilesUpload,
        onWhiteboardClick,
      ],
    );

    return (
      <Wrapper disabled={disabled}>
        {filesList?.length > 0 && (
          <Files>
            {filesList.map((file) => (
              <ChatImageTile
                key={file.url}
                imageUrl={file.url}
                loading={file.isUploading}
                canZoom
                canDelete
                onDelete={onImageDelete}
              />
            ))}
          </Files>
        )}
        <TransparentTextArea
          minRows={1}
          maxRows={maxRows ?? 6}
          placeholder={placeholderText}
          onKeyDown={handleKeyDown}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <Tools>
          <div className="left">
            {enableFilesUpload && (
              <GrAttachment
                size={16}
                onClick={dropzoneProps.open}
                style={{
                  pointerEvents: reachMaxFiles ? `none` : `auto`,
                  opacity: reachMaxFiles ? 0.5 : 1,
                }}
              />
            )}
            {enableWhiteboard && (
              <BiClipboard
                size={17}
                onClick={onWhiteboardClick}
                style={{
                  pointerEvents: reachMaxFiles ? `none` : `auto`,
                  opacity: reachMaxFiles ? 0.5 : 1,
                }}
              />
            )}
          </div>
          <div className="right">
            {enableExamples ? <Examples>Examples</Examples> : <div />}
            {enableVoiceInput ? (
              <HiOutlineMicrophone size={18} color="#a6afb8" />
            ) : (
              <div />
            )}
            <SendButton onClick={isLoading ? onStop : onSend}>
              {!isLoading && <FaArrowRight size={12} />}
              {isLoading && <MdStop size={15} />}
            </SendButton>
          </div>
        </Tools>
        <FileDropzone overlayVisible={showDropZone} {...dropzoneProps} />
        {isWhiteboardOpen && (
          <ToolWhiteboard
            isWhiteboardOpen={isWhiteboardOpen}
            setIsWhiteboardOpen={setIsWhiteboardOpen}
            onUploadSketch={onUploadSketch}
          />
        )}
      </Wrapper>
    );
  },
);
const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-radius: 3rem;
  box-shadow: 0 3px 10.5px 0 rgba(0, 0, 0, 0),
    0 8.5px 10.5px 0 rgba(151, 210, 206, 0.22),
    0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  border: solid 1px #e7eaed;
  background-color: #fff;
  padding: 2rem 2rem;
  gap: 1rem;
  overflow: hidden;

  &&[disabled] {
    background: #f6f5f4;

    && * {
      pointer-events: none !important;
    }
  }
`;

const Files = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1.5rem;
`;

const Tools = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  row-gap: 2rem;
  column-gap: 0;

  && > * {
    cursor: pointer;
    user-select: none;
  }

  && .left {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 1.5rem;
  }

  && .right {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1.5rem;
  }
`;

const TransparentTextArea = styled(TextareaAutosize)`
  && {
    height: 100%;
    width: 100%;
    background: transparent;
    border: none;
    appearance: none;
    outline: none;
    font-family: Eesti, sans-serif;
    font-size: 1.5rem;
    resize: none;
  }
`;

const Examples = styled.div`
  color: #a6afb8;
  text-decoration: underline;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
`;

const SendButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background: black;
  color: white;
  border-radius: 100%;
  height: 3.2rem;
  width: 3.2rem;
  border: none;
`;

export interface FileTileModel {
  url: string;
  isUploading: boolean;
}
